/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  Table2, KanbanSquare, Target, Network, Calendar, AlertTriangle, Loader2, ChevronRight,
  Settings as SettingsIcon, GripVertical,
} from 'lucide-react';
import { tasksApi, PRIORITY_COLORS, PRIORITY_LABELS, STATUS_LIST } from './tasksApi';
import { useWorkAreas } from '../../contexts/WorkAreaContext';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const PREF_KEY = 'orka.tasks.home.widgets';
const DEFAULT_VIEW_KEY = 'orka.tasks.default_view';

const ALL_WIDGETS = [
  { id: 'counts', title: 'Status overview', tab: 'kanban' },
  { id: 'top_priorities', title: 'Top priorities', tab: 'table' },
  { id: 'sprints', title: 'Active sprints', tab: 'sprints' },
  { id: 'epics', title: 'Epics', tab: 'epic' },
  { id: 'work_areas', title: 'Work area distribution', tab: 'kanban' },
  { id: 'overdue', title: 'Overdue tasks', tab: 'table' },
];

const loadWidgetOrder = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(PREF_KEY) || '[]');
    if (Array.isArray(saved) && saved.length) return saved;
  } catch { /* */ }
  return ALL_WIDGETS.map((w) => w.id);
};

export const HomeView = ({ onSwitchTab }) => {
  const { workAreaNames, workAreaColors } = useWorkAreas();
  const [tasks, setTasks] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [epics, setEpics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(loadWidgetOrder());
  const [defaultView, setDefaultView] = useState(localStorage.getItem(DEFAULT_VIEW_KEY) || 'home');

  const load = async () => {
    setLoading(true);
    try {
      const [t, s, e] = await Promise.all([tasksApi.listTasks(), tasksApi.listSprints(), tasksApi.listEpics()]);
      setTasks(t || []);
      setSprints(s || []);
      setEpics(e || []);
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const byStatus = {}; STATUS_LIST.forEach((s) => byStatus[s] = 0);
    const byPriority = { 0: 0, 1: 0, 2: 0, 3: 0 };
    const byWorkArea = {};
    let overdue = 0;
    const today = new Date().toISOString().slice(0, 10);
    tasks.forEach((t) => {
      const s = t.task_status || 'To Do';
      if (byStatus[s] !== undefined) byStatus[s]++;
      const p = t.task_priority ?? 3;
      if (byPriority[p] !== undefined) byPriority[p]++;
      const wa = t.work_area_id;
      if (wa) byWorkArea[wa] = (byWorkArea[wa] || 0) + 1;
      if (t.task_delivery_date && t.task_delivery_date.slice(0, 10) < today && t.task_status !== 'Done') overdue++;
    });
    return { byStatus, byPriority, byWorkArea, total: tasks.length, overdue };
  }, [tasks]);

  const topPriorities = useMemo(
    () => [...tasks]
      .filter((t) => t.task_status !== 'Done' && (t.task_priority ?? 3) <= 1)
      .sort((a, b) => (a.task_priority ?? 3) - (b.task_priority ?? 3))
      .slice(0, 8),
    [tasks]
  );

  const overdueTasks = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return tasks
      .filter((t) => t.task_status !== 'Done' && t.task_delivery_date && t.task_delivery_date.slice(0, 10) < today)
      .sort((a, b) => (a.task_delivery_date || '').localeCompare(b.task_delivery_date || ''))
      .slice(0, 8);
  }, [tasks]);

  const moveWidget = (idx, dir) => {
    const newOrder = [...order];
    const target = idx + dir;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[idx], newOrder[target]] = [newOrder[target], newOrder[idx]];
    setOrder(newOrder);
    localStorage.setItem(PREF_KEY, JSON.stringify(newOrder));
  };

  const updateDefaultView = (val) => {
    setDefaultView(val);
    localStorage.setItem(DEFAULT_VIEW_KEY, val);
    toast.success(`Default Tasks view set to ${val}`);
  };

  const renderWidget = (id, idx) => {
    const meta = ALL_WIDGETS.find((w) => w.id === id);
    if (!meta) return null;
    const goTo = () => onSwitchTab && onSwitchTab(meta.tab);
    return (
      <Card key={id} className="p-3 space-y-2 hover:border-violet-500/50 transition-colors group" data-testid={`widget-${id}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <button className="cursor-grab text-muted-foreground/40 hover:text-foreground" title="Reorder"
              onClick={() => moveWidget(idx, -1)} data-testid={`widget-${id}-up`}>
              <GripVertical className="w-3 h-3" />
            </button>
            <h3 className="text-xs font-semibold uppercase tracking-wider">{meta.title}</h3>
          </div>
          <button onClick={goTo} className="text-[10px] text-muted-foreground hover:text-violet-600 flex items-center gap-1"
            data-testid={`widget-${id}-open`}>
            Open <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <div onClick={goTo} className="cursor-pointer">
          {id === 'counts' && (
            <div className="grid grid-cols-5 gap-1.5">
              {STATUS_LIST.map((s) => (
                <div key={s} className="text-center bg-muted/50 rounded p-2">
                  <div className="text-lg font-bold">{stats.byStatus[s] || 0}</div>
                  <div className="text-[9px] text-muted-foreground line-clamp-1">{s}</div>
                </div>
              ))}
            </div>
          )}
          {id === 'top_priorities' && (
            <div className="space-y-1">
              {topPriorities.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No P0/P1 tasks pending — nice work.</p>
              ) : topPriorities.map((t) => (
                <div key={t.task_id} className="flex items-center gap-1.5 text-xs">
                  <Badge className={cn('text-[9px] h-4 px-1', PRIORITY_COLORS[t.task_priority ?? 3])}>{PRIORITY_LABELS[t.task_priority ?? 3]}</Badge>
                  <span className="truncate flex-1">{t.task_title}</span>
                </div>
              ))}
            </div>
          )}
          {id === 'sprints' && (
            <div className="space-y-1.5">
              {sprints.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No sprints yet.</p>
              ) : sprints.slice(0, 4).map((s) => {
                const sprintTasks = tasks.filter((t) => t.big_picture_id === s.item_id);
                const done = sprintTasks.filter((t) => t.task_status === 'Done').length;
                const pct = sprintTasks.length ? Math.round(done / sprintTasks.length * 100) : 0;
                return (
                  <div key={s.item_id} className="space-y-0.5">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="font-medium truncate">{s.title}</span>
                      <span className="text-muted-foreground">{done}/{sprintTasks.length}</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {id === 'epics' && (
            <div className="space-y-1">
              {epics.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">No epics yet.</p>
              ) : epics.slice(0, 5).map((e) => {
                const linked = tasks.filter((t) => t.big_picture_id === e.item_id).length;
                return (
                  <div key={e.item_id} className="flex items-center justify-between text-xs">
                    <span className="truncate flex-1">{e.title}</span>
                    <Badge variant="outline" className="text-[9px] h-4 ml-1">{linked}</Badge>
                  </div>
                );
              })}
            </div>
          )}
          {id === 'work_areas' && (
            <div className="space-y-1">
              {Object.entries(stats.byWorkArea).slice(0, 6).map(([id, count]) => (
                <div key={id} className="flex items-center gap-2 text-xs">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: workAreaColors[id] || '#94a3b8' }} />
                  <span className="truncate flex-1">{workAreaNames[id] || id}</span>
                  <span className="text-muted-foreground text-[10px]">{count}</span>
                </div>
              ))}
            </div>
          )}
          {id === 'overdue' && (
            <div className="space-y-1">
              {overdueTasks.length === 0 ? (
                <p className="text-xs text-emerald-600 italic">Nothing overdue. ✨</p>
              ) : overdueTasks.map((t) => (
                <div key={t.task_id} className="flex items-center gap-1.5 text-xs">
                  <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                  <span className="truncate flex-1">{t.task_title}</span>
                  <span className="text-[9px] text-muted-foreground">{t.task_delivery_date?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div data-testid="home-view" className="space-y-3">
      <Card className="p-3 flex items-center gap-3">
        <SettingsIcon className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Default Tasks view:</span>
        {['home', 'table', 'kanban', 'sprints', 'epic', 'graph'].map((v) => (
          <Button key={v} size="sm" variant={defaultView === v ? 'default' : 'outline'} className="h-7 text-xs"
            onClick={() => updateDefaultView(v)} data-testid={`default-view-${v}`}>
            {v.charAt(0).toUpperCase() + v.slice(1)}
          </Button>
        ))}
        <span className="text-[10px] text-muted-foreground ml-auto">Saved on this device</span>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-muted-foreground text-xs">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> loading
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3" data-testid="hero-counts">
            <Card className="p-3 text-center">
              <div className="text-2xl font-bold">{stats.total}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Total tasks</div>
            </Card>
            <Card className="p-3 text-center">
              <div className="text-2xl font-bold text-amber-600">{stats.byStatus['In Progress'] || 0}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">In progress</div>
            </Card>
            <Card className="p-3 text-center">
              <div className="text-2xl font-bold text-emerald-600">{stats.byStatus['Done'] || 0}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Done</div>
            </Card>
            <Card className={cn('p-3 text-center', stats.overdue > 0 && 'border-red-500/50 bg-red-500/5')}>
              <div className={cn('text-2xl font-bold', stats.overdue > 0 ? 'text-red-600' : '')}>{stats.overdue}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Overdue</div>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="widgets-grid">
            {order.map((id, idx) => renderWidget(id, idx))}
          </div>
        </>
      )}
    </div>
  );
};

export default HomeView;
